import jsPDF from 'jspdf';
import { AnalysisResult } from './api';
import { toast } from "sonner";

export function exportToPdf(result: AnalysisResult, title: string = "AI Analysis Report") {
  try {
    const doc = new jsPDF();
    
    // Set up document
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 20;
    
    // Add title
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 100);
    doc.text(title, margin, yPos);
    yPos += 10;
    
    // Add date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, yPos);
    yPos += 15;
    
    // Add header line
    doc.setDrawColor(0, 128, 255);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    
    // Add analysis result
    doc.setFontSize(12);
    doc.text("Analysis Result:", margin, yPos);
    yPos += 7;
    
    // Set color based on type
    let typeColor;
    switch (result.type) {
      case 'factual':
        typeColor = [0, 128, 0]; // Green
        break;
      case 'misinformation':
        typeColor = [200, 0, 0]; // Red
        break;
      case 'misleading':
        typeColor = [200, 128, 0]; // Orange
        break;
      default:
        typeColor = [100, 100, 100]; // Gray
    }
    
    doc.setFontSize(11);
    doc.setTextColor(typeColor[0], typeColor[1], typeColor[2]);
    doc.text(`Classification: ${result.type.toUpperCase()} (Confidence: ${result.confidence}%)`, margin, yPos);
    yPos += 10;
    
    // Add explanation
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Explanation:", margin, yPos);
    yPos += 5;
    
    const explanationLines = doc.splitTextToSize(result.explanation, contentWidth);
    doc.text(explanationLines, margin, yPos);
    yPos += explanationLines.length * 5 + 10;
    
    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    // Add suggestions
    doc.setFontSize(11);
    doc.text("Suggestions:", margin, yPos);
    yPos += 7;
    
    // Add each suggestion as bullet points
    doc.setFontSize(10);
    result.suggestions.forEach((suggestion, index) => {
      const suggestionLines = doc.splitTextToSize(`• ${suggestion}`, contentWidth - 5);
      
      // Check if we need a new page
      if (yPos + (suggestionLines.length * 5) > 280) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.text(suggestionLines, margin, yPos);
      yPos += suggestionLines.length * 5 + 3;
    });
    
    // Add source analysis if available
    if (result.sourceAnalysis) {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos += 5;
      doc.setFontSize(12);
      doc.text("Source Credibility Analysis:", margin, yPos);
      yPos += 7;
      
      doc.setFontSize(11);
      doc.text(`Overall Credibility Score: ${result.sourceAnalysis.credibility}%`, margin, yPos);
      yPos += 10;
      
      // Add factors
      doc.setFontSize(10);
      result.sourceAnalysis.factors.forEach((factor, index) => {
        // Check if we need a new page
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.text(`${factor.name} (${factor.score}%)`, margin, yPos);
        yPos += 5;
        
        const factorLines = doc.splitTextToSize(factor.explanation, contentWidth - 5);
        doc.text(factorLines, margin + 5, yPos);
        yPos += factorLines.length * 5 + 5;
      });
    }
    
    // Add footer with disclaimer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("This analysis was generated by an AI model and should be verified with additional sources.", margin, 285);
    
    // Save the PDF
    doc.save("truth-nexus-analysis.pdf");
  } catch (error) {
    console.error("Error generating PDF:", error);
    toast.error("Failed to generate PDF. Please try again.");
  }
}
